import React, { useState } from 'react';

export type DeployEnvironment = 'staging' | 'production';
export type VersionAction = 'deploy' | 'rollback';

// One environment's view of a version, as the backend reports it. `current`
// is what that environment serves now; `succeeded` is whether this commit has
// ever deployed there.
interface EnvironmentState {
  status: 'success' | 'failed' | 'running' | 'pending';
  at: string;
  url: string;
  succeeded: boolean;
  current: boolean;
}

// What the environment's button does for this version, or why there is none.
// Decided by the backend, which applies the same rules the pipeline does, so
// a button is only shown when the workflow would accept it.
interface ActionState {
  action: VersionAction | null;
  reason: string | null;
}

export interface Version {
  sha: string;
  shortSha: string;
  message: string;
  author: string;
  committedAt: string;
  branch: string;
  onMain: boolean;
  pullRequest: { number: number; url: string } | null;
  url: string;
  staging: EnvironmentState | null;
  production: EnvironmentState | null;
  actions: Record<DeployEnvironment, ActionState>;
}

interface VersionsTableProps {
  versions: Version[];
  busy: boolean;
  onAction: (version: Version, environment: DeployEnvironment, action: VersionAction) => void;
}

type Filter = 'all' | 'deployed' | 'undeployed' | 'branches';

const FILTERS: Record<Filter, { label: string; matches: (v: Version) => boolean }> = {
  all: { label: 'All versions', matches: () => true },
  deployed: { label: 'Deployed somewhere', matches: (v) => Boolean(v.staging || v.production) },
  undeployed: { label: 'Never deployed', matches: (v) => !v.staging && !v.production },
  branches: { label: 'Branches (not on main)', matches: (v) => !v.onMain },
};

const STATUS_COLORS: Record<EnvironmentState['status'], string> = {
  success: 'bg-scarf-green/20 text-scarf-green',
  failed: 'bg-brand-red/20 text-brand-red',
  running: 'bg-brand-blue-light/20 text-brand-blue-light',
  pending: 'bg-gray-100 text-gray-600',
};

const EnvironmentCell: React.FC<{ state: EnvironmentState | null }> = ({ state }) => {
  if (!state) {
    return <span className="text-gray-400 text-sm">Not deployed</span>;
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex items-center gap-2">
        {state.current && (
          <span className="px-2 py-1 rounded text-xs font-bold bg-deep-navy text-white">LIVE</span>
        )}
        <a
          href={state.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`px-2 py-1 rounded text-xs font-bold hover:underline ${STATUS_COLORS[state.status]}`}
        >
          {state.status}
        </a>
      </div>
      <span className="text-xs text-gray-500">{new Date(state.at).toLocaleString()}</span>
    </div>
  );
};

const BUTTON_STYLES: Record<DeployEnvironment, Record<VersionAction, string>> = {
  staging: {
    deploy: 'bg-brand-blue-light hover:bg-brand-blue text-white',
    rollback: 'bg-deep-navy hover:bg-deep-navy/90 text-white',
  },
  production: {
    deploy: 'bg-brand-red hover:bg-brand-red/90 text-white',
    rollback: 'bg-deep-navy hover:bg-deep-navy/90 text-white',
  },
};

const BUTTON_LABELS: Record<DeployEnvironment, Record<VersionAction, string>> = {
  staging: { deploy: 'Deploy to staging', rollback: 'Roll back staging' },
  production: { deploy: 'Promote to production', rollback: 'Roll back production' },
};

const VersionsTable: React.FC<VersionsTableProps> = ({ versions, busy, onAction }) => {
  const [filter, setFilter] = useState<Filter>('all');

  const shown = versions.filter(FILTERS[filter].matches);

  const actionButton = (version: Version, environment: DeployEnvironment) => {
    const { action, reason } = version.actions[environment];

    // No action: say why rather than leaving a gap, so "why can't I promote
    // this?" is answered on the row.
    if (!action) {
      return reason ? <span className="text-xs text-gray-400">{reason}</span> : null;
    }

    return (
      <button
        onClick={() => onAction(version, environment, action)}
        disabled={busy}
        className={`px-3 py-1 rounded text-xs font-bold whitespace-nowrap disabled:opacity-50 transition-colors ${BUTTON_STYLES[environment][action]}`}
      >
        {BUTTON_LABELS[environment][action]}
      </button>
    );
  };

  return (
    <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-2">
        <h3 className="text-xl font-title font-bold text-deep-navy">Versions</h3>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as Filter)}
          aria-label="Filter versions"
          className="bg-frost-white border border-ice-teal-light rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:border-brand-blue"
        >
          {(Object.keys(FILTERS) as Filter[]).map((key) => (
            <option key={key} value={key}>{FILTERS[key].label}</option>
          ))}
        </select>
      </div>
      <p className="text-gray-500 text-sm mb-4">
        One row per commit: recent merges to main, the head of each open pull request, and
        anything either environment has deployed. Branches can go to staging; only main
        reaches production, and only after staging. A rollback redeploys that exact commit
        through the pipeline.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-gray-800">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-deep-navy font-bold">Version</th>
              <th className="text-left py-3 px-4 text-deep-navy font-bold">Change</th>
              <th className="text-left py-3 px-4 text-deep-navy font-bold">Staging</th>
              <th className="text-left py-3 px-4 text-deep-navy font-bold">Production</th>
              <th className="text-left py-3 px-4 text-deep-navy font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((version) => (
              <tr key={version.sha} className="border-b border-gray-100 align-top">
                <td className="py-3 px-4">
                  <a
                    href={version.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-sm font-bold text-brand-blue hover:underline"
                  >
                    {version.shortSha}
                  </a>
                  <div className="font-mono text-xs text-gray-500 mt-1">
                    {version.pullRequest ? (
                      <a href={version.pullRequest.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {version.branch} (#{version.pullRequest.number})
                      </a>
                    ) : (
                      version.branch || 'unknown branch'
                    )}
                  </div>
                </td>
                <td className="py-3 px-4 text-sm max-w-md">
                  <div className="font-bold text-deep-navy">{version.message}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {version.author} &middot; {new Date(version.committedAt).toLocaleString()}
                  </div>
                </td>
                <td className="py-3 px-4"><EnvironmentCell state={version.staging} /></td>
                <td className="py-3 px-4"><EnvironmentCell state={version.production} /></td>
                <td className="py-3 px-4">
                  <div className="flex flex-col items-start gap-2">
                    {actionButton(version, 'staging')}
                    {actionButton(version, 'production')}
                  </div>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr>
                <td colSpan={5} className="py-6 px-4 text-center text-gray-500 text-sm">
                  No versions match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VersionsTable;
